import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { AlertMessages } from '../AlertMessages';
import { getCashClosing } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/formatCoins';
import { FormContainer } from './styles';

export const CashClosingForm = () => {
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  // Carregar filial do localStorage, ou usar ALTO_BRANCO como padrão apenas na primeira vez
  const [branch, setBranch] = useState(() => {
    try {
      const savedBranch = localStorage.getItem('canteen_filial');
      if (savedBranch && (savedBranch === 'ALTO_BRANCO' || savedBranch === 'CONCEICAO')) {
        return savedBranch;
      }
    } catch (error) {
      console.error('[CashClosingForm] Erro ao carregar filial do localStorage:', error);
    }
    const defaultBranch = 'ALTO_BRANCO';
    // Salvar padrão no localStorage na primeira vez
    try {
      localStorage.setItem('canteen_filial', defaultBranch);
    } catch (error) {
      console.error('[CashClosingForm] Erro ao salvar filial padrão:', error);
    }
    return defaultBranch;
  });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });

  // Sincronizar data fim com data início quando data início mudar
  useEffect(() => {
    if (startDate) {
      setEndDate(startDate);
    }
  }, [startDate]);

  // Garantir que a filial padrão seja salva no localStorage na primeira vez e sincronizar estado
  useEffect(() => {
    try {
      const savedBranch = localStorage.getItem('canteen_filial');
      if (!savedBranch || (savedBranch !== 'ALTO_BRANCO' && savedBranch !== 'CONCEICAO')) {
        const defaultBranch = branch || 'ALTO_BRANCO';
        localStorage.setItem('canteen_filial', defaultBranch);
        if (branch !== defaultBranch) {
          setBranch(defaultBranch);
        }
      } else if (savedBranch !== branch) {
        setBranch(savedBranch);
      }
    } catch (error) {
      console.error('[CashClosingForm] Erro ao salvar filial padrão no localStorage:', error);
    }
  }, []); // Apenas na montagem

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getBranchLabel = (b) => {
    return b === 'CONCEICAO' ? 'Conceição' : b === 'ALTO_BRANCO' ? 'Alto Branco' : b || '-';
  };

  const handlePrint = () => {
    if (!report) return;
    const topups = report.manualTopups || [];
    const itemsSold = report.itemsSold || [];
    const itemsMidpoint = Math.ceil(itemsSold.length / 2);
    const itemsLeftColumn = itemsSold.slice(0, itemsMidpoint);
    const itemsRightColumn = itemsSold.slice(itemsMidpoint);

    const renderItemsTable = (items) => {
      if (!items.length) {
        return `
          <table>
            <thead>
              <tr>
                <th>Produto</th>
                <th class="valor">Qtd</th>
                <th class="valor">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan="3" style="text-align:center; color:#666;">Sem itens</td>
              </tr>
            </tbody>
          </table>
        `;
      }

      return `
        <table>
          <thead>
            <tr>
              <th>Produto</th>
              <th class="valor">Qtd</th>
              <th class="valor">Total</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((item) => `
              <tr>
                <td>${(item.name || '-').replace(/</g, '&lt;')}</td>
                <td class="valor">${item.quantity || 0}</td>
                <td class="valor">${formatCurrency(item.total || 0)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    };

    const html = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Fechamento de Caixa</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; color: #333; }
    h1 { font-size: 18px; margin-bottom: 8px; border-bottom: 2px solid #0b4f6c; padding-bottom: 6px; }
    .meta { margin-bottom: 16px; color: #666; }
    .resumo { margin: 16px 0; }
    .resumo table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    .resumo th, .resumo td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
    .resumo th { background: #f5f5f5; font-weight: 600; }
    .resumo .valor { text-align: right; }
    h2 { font-size: 14px; margin: 20px 0 8px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ddd; padding: 6px 10px; text-align: left; font-size: 11px; }
    th { background: #f5f5f5; font-weight: 600; }
    .valor { text-align: right; }
    .items-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .total-geral { margin-top: 16px; font-weight: 700; font-size: 14px; }
    .avoid-break { break-inside: avoid; page-break-inside: avoid; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <h1>Fechamento de Caixa</h1>
  <div class="meta">
    <p><strong>Período:</strong> ${startDate.format('DD/MM/YYYY')} até ${endDate.format('DD/MM/YYYY')}</p>
    <p><strong>Filial:</strong> ${getBranchLabel(branch)}</p>
    <p><strong>Emitido em:</strong> ${dayjs().format('DD/MM/YYYY HH:mm')}</p>
  </div>
  <div class="resumo">
    <table>
      <tr><th>Resumo</th><th class="valor">Valor</th><th>Quantidade</th></tr>
      <tr><td>Vendas Estudantes</td><td class="valor">${formatCurrency(report.summary?.totalSalesStudents || 0)}</td><td>${report.summary?.totalOrdersStudents || 0} pedidos</td></tr>
      <tr><td>Vendas Colaboradores</td><td class="valor">${formatCurrency(report.summary?.totalSalesEmployees || 0)}</td><td>${report.summary?.totalOrdersEmployees || 0} pedidos</td></tr>
      <tr><td>Vendas Avulsas</td><td class="valor">${formatCurrency(report.summary?.totalSalesAvulsas || 0)}</td><td>${report.summary?.totalOrdersAvulsas || 0} pedidos</td></tr>
      <tr><td>Recargas Manuais</td><td class="valor">${formatCurrency(report.summary?.totalManualTopups || 0)}</td><td>${report.summary?.totalTopups || 0} recargas</td></tr>
      <tr class="total-geral"><td>Total Geral de Vendas</td><td class="valor">${formatCurrency(report.summary?.totalSales || 0)}</td><td>${report.summary?.totalOrders || 0} pedidos</td></tr>
    </table>
  </div>
  <h2>Itens Vendidos</h2>
  ${itemsSold.length > 0 ? `
    <div class="items-grid avoid-break">
      <div>${renderItemsTable(itemsLeftColumn)}</div>
      <div>${renderItemsTable(itemsRightColumn)}</div>
    </div>
  ` : '<p>Nenhum item vendido no período.</p>'}

  <h2>Recargas Manuais</h2>
  ${topups.length > 0 ? `
  <table>
    <thead><tr><th>Data/Hora</th><th>Estudante</th><th class="valor">Valor</th><th>Registrado por</th><th>Observação</th></tr></thead>
    <tbody>
      ${topups.map((t) => `
        <tr>
          <td>${formatDate(t.createdAt)}</td>
          <td>${(t.studentName || '-').replace(/</g, '&lt;')}</td>
          <td class="valor">${formatCurrency(t.amountCoins)}</td>
          <td>${(t.createdByName || 'Sistema').replace(/</g, '&lt;')}</td>
          <td>${(t.note || '-').replace(/</g, '&lt;')}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
  ` : '<p>Nenhuma recarga manual no período.</p>'}
</body>
</html>`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setAlertState({ show: true, type: 'error', message: 'Permita pop-ups para imprimir.' });
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Selecione as datas de início e fim',
      });
      return;
    }

    if (startDate > endDate) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Data início deve ser anterior à data fim',
      });
      return;
    }

    try {
      setLoading(true);
      setPage(0); // Resetar paginação ao gerar novo relatório
      const start = startDate.format('YYYY-MM-DD');
      const end = endDate.format('YYYY-MM-DD');
      
      const data = await getCashClosing(start, end, branch); // Passar branch ao buscar fechamento
      setReport(data);
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FormContainer>
        <Typography variant="h6" sx={{ color: '#0b4f6c', fontWeight: 600, mb: 3 }}>
          Fechamento de Caixa
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Data Início"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Data Fim"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="branch-select-label">Filial</InputLabel>
                <Select
                  labelId="branch-select-label"
                  id="branch-select"
                  value={branch}
                  label="Filial"
                  onChange={(e) => {
                    const newBranch = e.target.value;
                    if (newBranch === 'ALTO_BRANCO' || newBranch === 'CONCEICAO') {
                      setBranch(newBranch);
                      // Salvar no localStorage
                      try {
                        localStorage.setItem('canteen_filial', newBranch);
                      } catch (error) {
                        console.error('[CashClosingForm] Erro ao salvar filial no localStorage:', error);
                      }
                    }
                  }}
                >
                  <MenuItem value="ALTO_BRANCO">Alto Branco</MenuItem>
                  <MenuItem value="CONCEICAO">Conceição</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={loading || !startDate || !endDate}
            sx={{
              bgcolor: '#1e958c',
              padding: 1.5,
              fontSize: '16px',
              fontWeight: 600,
              '&:hover': { bgcolor: '#0b4f6c' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Gerar Relatório'}
          </Button>
          {report && (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                borderColor: '#1e958c',
                color: '#1e958c',
                '&:hover': { borderColor: '#0b4f6c', bgcolor: 'rgba(30, 149, 140, 0.08)' },
              }}
            >
              Imprimir
            </Button>
          )}
        </Box>

        {report && (
          <>
            {/* Resumo */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Período
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {startDate.format('DD/MM/YYYY')} até {endDate.format('DD/MM/YYYY')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#e8f5e9', border: '1px solid #81c784' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Vendas Estudantes
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {formatCurrency(report.summary.totalSalesStudents || 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.summary.totalOrdersStudents || 0} pedidos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#fce4ec', border: '1px solid #f48fb1' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Vendas Colaboradores
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#c2185b' }}>
                      {formatCurrency(report.summary.totalSalesEmployees || 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.summary.totalOrdersEmployees || 0} pedidos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#ede7f6', border: '1px solid #b39ddb' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Vendas Avulsas
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#5e35b1' }}>
                      {formatCurrency(report.summary.totalSalesAvulsas || 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.summary.totalOrdersAvulsas || 0} pedidos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#fff3e0', border: '1px solid #ffb74d' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Recargas Manuais
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#e65100' }}>
                      {formatCurrency(report.summary.totalManualTopups || 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.summary.totalTopups || 0} recargas
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#f5f5f5', border: '2px solid #424242' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Total Geral de Vendas
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#212121' }}>
                      {formatCurrency(report.summary.totalSales || 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.summary.totalOrders || 0} pedidos no total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Itens Vendidos */}
            {report.itemsSold && report.itemsSold.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#0b4f6c', fontWeight: 600, mb: 2 }}>
                  Itens Vendidos
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Produto</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Quantidade</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.itemsSold.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{item.name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(item.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Detalhes dos Pedidos */}
            {report.orders && report.orders.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ color: '#0b4f6c', fontWeight: 600, mb: 2 }}>
                  Detalhes dos Pedidos ({report.orders.length} {report.orders.length === 1 ? 'pedido' : 'pedidos'})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Data/Hora</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Tipo</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Cliente</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Itens</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.orders
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>{formatDate(order.createdAt)}</TableCell>
                          <TableCell>
                            <Chip
                              label={
                                order.ownerType === 'AVULSA'
                                  ? 'Avulsa'
                                  : order.ownerType === 'EMPLOYEE'
                                    ? 'Colaborador'
                                    : 'Estudante'
                              }
                              size="small"
                              color={
                                order.ownerType === 'AVULSA'
                                  ? 'warning'
                                  : order.ownerType === 'EMPLOYEE'
                                    ? 'secondary'
                                    : 'primary'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {order.studentName || order.employeeName || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {order.items.map((item, idx) => (
                                <Typography key={idx} variant="body2">
                                  {item.productName} × {item.quantity} = {formatCurrency(item.lineTotal)}
                                </Typography>
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(order.totalCoins)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={report.orders.length}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  labelRowsPerPage="Itens por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
              </Box>
            )}

            {report.orders && report.orders.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="textSecondary">
                  Nenhuma venda encontrada no período selecionado
                </Typography>
              </Box>
            )}

            {/* Recargas Manuais */}
            {report.manualTopups && report.manualTopups.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ color: '#0b4f6c', fontWeight: 600, mb: 2 }}>
                  Recargas Manuais ({report.manualTopups.length} {report.manualTopups.length === 1 ? 'recarga' : 'recargas'})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Data/Hora</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Estudante</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Registrado por</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Observação</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.manualTopups.map((topup) => (
                        <TableRow key={topup.id} hover>
                          <TableCell>{formatDate(topup.createdAt)}</TableCell>
                          <TableCell>{topup.studentName}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            {formatCurrency(topup.amountCoins)}
                          </TableCell>
                          <TableCell>{topup.createdByName || 'Sistema'}</TableCell>
                          <TableCell>{topup.note || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </FormContainer>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
